import { Dropdown, Button, Checkbox, Input } from 'antd';
import { CaretDownIcon } from '@phosphor-icons/react';
import { useState, useRef, useCallback } from 'react';
import { FacetDTOType } from '../../../../schemas/facets';
import { useVirtualizer } from '@tanstack/react-virtual';

interface IFacetDropdownProps {
  facet: FacetDTOType;
  selectedValues: Array<string>;
  onChange: (values: Array<string>) => void;
  size?: 'small' | 'middle' | 'large';
  enableSearch?: boolean;
}

const ITEM_HEIGHT = 32;

const FacetDropdown = ({
  facet,
  selectedValues,
  onChange,
  size = 'middle',
  enableSearch = true,
}: IFacetDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const options = facet.values.map(value => ({
    label: typeof value.count === 'number' ? `${value.value} (${value.count})` : value.value,
    value: value.id,
  }));

  const filteredOptions =
    enableSearch && search
      ? options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()))
      : options;

  const label =
    selectedValues.length === 0
      ? facet.key.displayName
      : `${facet.key.displayName} (${selectedValues.length})`;

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => ITEM_HEIGHT, []),
    overscan: 5,
    measureElement: (element, _roEntry, instance) => {
      const cachedSize = instance.measurementsCache[instance.indexFromElement(element)].size;
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0) {
        return Math.round(rect.height);
      }
      return cachedSize;
    },
    // measureElement: el => el.getBoundingClientRect().height,
  });

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            minWidth: 180,
            background: '#fff',
            maxHeight: '300px',
            overflowY: 'auto',
            borderRadius: 20,
            boxShadow: '0px 4px 34px 0px rgba(0, 0, 0, 0.1)',
          }}
        >
          {enableSearch && (
            <Input
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ marginBottom: 12 }}
              allowClear
              size="small"
              variant="borderless"
            />
          )}
          <div
            ref={parentRef}
            style={{
              height: '220px',
              overflowY: 'auto',
              position: 'relative',
              width: '100%',
            }}
          >
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map(virtualRow => {
                const opt = filteredOptions[virtualRow.index];
                const isChecked = selectedValues.includes(opt.value);
                return (
                  <div
                    data-index={virtualRow.index}
                    key={opt.value}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      padding: '6px 0',
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={e => {
                        const newValues = e.target.checked
                          ? [...selectedValues, opt.value]
                          : selectedValues.filter(v => v !== opt.value);
                        onChange(newValues);
                      }}
                    >
                      {opt.label}
                    </Checkbox>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      trigger={['click']}
    >
      <Button
        size={size}
        variant="solid"
        style={{
          minWidth: 150,
          textAlign: 'left',
          backgroundColor: '#F5F5F5',
          color: '#171717',
          border: 'none',
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
        }}
        onClick={e => e.preventDefault()}
      >
        {label} <CaretDownIcon style={{ float: 'right' }} />
      </Button>
    </Dropdown>
  );
};

export default FacetDropdown;
